import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.././../store';

// Define notification type
export interface Notification {
  id: string;
  title: string;
  message: string;
  time: string;
  vehicleId?: string;
  read: boolean;
  type?: string;
}

// Initial state
interface NotificationState {
  notifications: Notification[];
  totalUnread: number;
  loading: boolean;
  error: string | null;
}

// Load read notification IDs from localStorage
const loadReadNotificationIds = (): string[] => {
  try {
    const stored = localStorage.getItem('readNotificationIds');
    const ids = stored ? JSON.parse(stored) : [];
    // Clean up old IDs on load
    const cleanedIds = cleanupReadNotificationIds(ids);
    if (cleanedIds.length !== ids.length) {
      localStorage.setItem('readNotificationIds', JSON.stringify(cleanedIds));
    }
    return cleanedIds;
  } catch (error) {
    return [];
  }
};

// Cleanup old read notification IDs (keep only recent ones)
const cleanupReadNotificationIds = (currentNotificationIds: string[]): string[] => {
  // Keep only the most recent 100 read notification IDs to prevent localStorage bloat
  if (currentNotificationIds.length > 100) {
    return currentNotificationIds.slice(-100);
  }
  return currentNotificationIds;
};

const initialState: NotificationState = {
  notifications: [],
  totalUnread: 0,
  loading: false,
  error: null,
};

// Slice
export const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, { payload }: PayloadAction<Notification[] | any>) => {
      // Handle case where notifications come as nested array [Array(1)]
      let notifications = payload;

      // Flatten nested array structure if needed
      if (Array.isArray(notifications) && notifications.length === 1 && Array.isArray(notifications[0])) {
        notifications = notifications[0];
      }

      // Load read notification IDs from localStorage
      const readNotificationIds = loadReadNotificationIds();

      // Only update if we have actual notifications data
      // Don't clear existing notifications if API returns empty data
      if (Array.isArray(notifications) && notifications.length > 0) {
        // Preserve read status from existing notifications and localStorage
        const updatedNotifications = notifications.map((newNotification: any) => {
          // Normalize isRead to read
          const normalizedNotification = {
            ...newNotification,
            read: newNotification.read === true || newNotification.isRead === true
          };

          // Check if this notification already exists in the state
          const existingNotification = state.notifications.find(
            (n: any) => n.id === normalizedNotification.id
          );

          // If it exists and is marked as read, preserve the read status
          if (existingNotification && existingNotification.read) {
            return {
              ...normalizedNotification,
              read: true
            };
          }

          // Check if notification ID is in localStorage read list
          if (readNotificationIds.includes(normalizedNotification.id)) {
            return {
              ...normalizedNotification,
              read: true
            };
          }

          return normalizedNotification;
        });

        state.notifications = updatedNotifications;
      } else if (!Array.isArray(notifications) && notifications) {
        // Handle single notification object
        state.notifications = [notifications];
      }
      // If notifications is empty array or null/undefined, keep existing notifications
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.totalUnread = Math.max(0, state.totalUnread - 1);

        // Save read notification ID to localStorage
        try {
          const readNotificationIds = loadReadNotificationIds();
          if (!readNotificationIds.includes(action.payload)) {
            readNotificationIds.push(action.payload);
            const cleanedReadIds = cleanupReadNotificationIds(readNotificationIds);
            localStorage.setItem('readNotificationIds', JSON.stringify(cleanedReadIds));
          }
        } catch (error) {
          console.error('Failed to save read notification ID to localStorage:', error);
        }
      }
    },
    markAllAsRead: (state) => {
      const readNotificationIds = loadReadNotificationIds();
      const newReadIds: string[] = [];

      state.notifications.forEach(notification => {
        if (!notification.read) {
          notification.read = true;
          if (!readNotificationIds.includes(notification.id)) {
            newReadIds.push(notification.id);
          }
        }
      });

      // Save new read notification IDs to localStorage
      if (newReadIds.length > 0) {
        try {
          const updatedReadIds = [...readNotificationIds, ...newReadIds];
          const cleanedReadIds = cleanupReadNotificationIds(updatedReadIds);
          localStorage.setItem('readNotificationIds', JSON.stringify(cleanedReadIds));
        } catch (error) {
          console.error('Failed to save read notification IDs to localStorage:', error);
        }
      }
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setTotalUnread: (state, action: PayloadAction<number>) => {
      state.totalUnread = action.payload;
    }
  }
});

// Export actions
export const { addNotification, markAsRead, markAllAsRead, removeNotification, clearNotifications, setTotalUnread } = notificationSlice.actions;

// Selectors
export const getNotifications = (state: RootState) => state.notifications.notifications || [];
export const getUnreadNotificationsCount = (state: RootState) => state.notifications.totalUnread || 0;
export const getNotificationsLoading = (state: RootState) => state.notifications.loading || false;
export const getNotificationsError = (state: RootState) => state.notifications.error || null;

export default notificationSlice.reducer;